// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, layoutContainer, xScreen } from "@pacific/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasCategoryContext;
export interface CategoryInformationStyles {
    wrapper?: InjectableCss;
    container?: InjectableCss;
    titleDescriptionWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    button?: ButtonPresentationProps;
    imageWrapper?: InjectableCss;
    box?: InjectableCss;
}
export const categoryInformationStyles: CategoryInformationStyles = {
    wrapper: {
        css: css`
            background-color: ${color("common.ui2")};
            padding-top: 16px;
            padding-bottom: 52px;
            ${xScreen("lte", "md")} {
                padding-top: 16px;
                padding-bottom: 24px;
            }
            ${xScreen("lte", "sm")} {
                padding-bottom: 18px;
            }
        `,
    },
    container: {
        css: css`
            ${layoutContainer}
            nav[data-test-selector="pageBreadcrumbs"] {
                margin-bottom: 20px;
                width: 100%;
            }
            display: flex;
            flex-wrap: wrap;
            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },
    titleDescriptionWrapper: {
        css: css`
            width: calc(50% - 86px);
            margin-right: 86px;
            &:empty {
                display: none;
            }
            ${xScreen("lte", "md")} {
                width: 100%;
                margin-right: 0;
                order: 4;
            }
        `,
    },
    title: {
        variant: "h1",
        css: css`
            margin-bottom: 16px;
        `,
    },
    button: {
        css: css`
            min-height: 34px;
            padding: 0 15px;
        `,
    },
    imageWrapper: {
        css: css`
            width: 50%;
            height: 208px;
            display: inline-flex;
            position: relative;
            ${xScreen("lte", "md")} {
                width: 100%;
                margin-bottom: 24px;
                height: 242px;
            }
            ${xScreen("lte", "sm")} {
                margin-bottom: 20px;
                height: 208px;
            }
        `,
    },
    box: {
        css: css`
            width: 100%;
            height: 196px;
            margin-top: 12px;
            margin-left: 12px;
            width: calc(100% - 12px);
            border: 2px solid ${color("secondary.main")};
            ${xScreen("lte", "md")} {
                margin-top: 14px;
                margin-left: 14px;
                height: 228px;
                width: calc(100% - 14px);
            }
            ${xScreen("lte", "sm")} {
                margin-top: 0;
                margin-left: 39px;
                height: 208px;
                width: calc(100% - 39px);
            }
        `,
    },
};

export const CategoryInformation = ({ category }: Props) => {
    const styles = categoryInformationStyles;

    if (!category) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.container}>
                <PageBreadcrumbs />
                <Hidden above="md">
                    <Typography {...styles.title}>{category.shortDescription}</Typography>
                </Hidden>
                <StyledWrapper {...styles.titleDescriptionWrapper}>
                    <Hidden below="lg">
                        <Typography {...styles.title}>{category.shortDescription}</Typography>
                    </Hidden>
                    {createWidgetElement("Category/CategoryDetailBanner", { fields: {} })}
                </StyledWrapper>
                <StyledWrapper {...styles.imageWrapper}>
                    <StyledWrapper {...styles.box}></StyledWrapper>
                    {createWidgetElement("Category/CategoryImage", { fields: {} })}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: withCategory(CategoryInformation),
    definition: {
        group: "Category Details",
        allowedContexts: ["CategoryDetailsPage"],
    },
};
export default widgetModule;
